import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { defer, EMPTY, expand, Observable, reduce } from 'rxjs';
import {
  GymProfileCompleteness,
  NetworkFilter,
  Page,
  ProfileCompletenessExportRequest,
  ProfileCompletenessRequest,
  ProfileCompletenessSummary,
} from '../model/profile-completeness';

const BASE_URL =
  '/upstream/gymfinder/internal/admin/v1/gym-profile-completeness';

const EXPORT_PAGE_SIZE = 500;

@Injectable({ providedIn: 'root' })
export class ProfileCompletenessService {
  private readonly httpClient = inject(HttpClient);

  public getPage(
    request: ProfileCompletenessRequest
  ): Observable<Page<GymProfileCompleteness>> {
    let params = new HttpParams()
      .set('sorting', request.sorting)
      .set('page', request.page)
      .set('pageSize', request.pageSize);

    if (request.searchTerm) {
      params = params.set('searchTerm', request.searchTerm);
    }
    params = appendNetworks(params, request.networkFilter ?? []);

    return this.httpClient.get<Page<GymProfileCompleteness>>(BASE_URL, {
      withCredentials: true,
      params,
    });
  }

  public getAll(
    filter: ProfileCompletenessExportRequest = {}
  ): Observable<GymProfileCompleteness[]> {
    const requestedPageSize = EXPORT_PAGE_SIZE;
    const sorting = filter.sorting ?? 'HOURS';

    return defer(() => {
      let totalFetched = 0;

      return this.getPage({
        ...filter,
        sorting,
        page: 0,
        pageSize: requestedPageSize,
      }).pipe(
        expand(pageResult => {
          totalFetched += pageResult.items.length;
          const responsePageSize =
            pageResult.pageSize > 0 ? pageResult.pageSize : requestedPageSize;
          const effectivePageSize =
            pageResult.items.length > 0 &&
            pageResult.items.length < responsePageSize &&
            totalFetched < pageResult.total
              ? pageResult.items.length
              : responsePageSize;
          const nextPageIndex = pageResult.pageIndex + 1;

          if (totalFetched < pageResult.total && pageResult.items.length > 0) {
            return this.getPage({
              ...filter,
              sorting,
              page: nextPageIndex,
              pageSize: effectivePageSize,
            });
          }
          return EMPTY;
        }),
        reduce(
          (acc: GymProfileCompleteness[], pageResult) =>
            acc.concat(pageResult.items),
          []
        )
      );
    });
  }

  public getSummary(
    networkFilter: readonly NetworkFilter[]
  ): Observable<ProfileCompletenessSummary> {
    return this.httpClient.get<ProfileCompletenessSummary>(
      `${BASE_URL}/summary`,
      {
        withCredentials: true,
        params: appendNetworks(new HttpParams(), networkFilter),
      }
    );
  }
}

function appendNetworks(
  params: HttpParams,
  networkFilter: readonly NetworkFilter[]
): HttpParams {
  return networkFilter.reduce(
    (acc, network) => acc.append('networkFilter', network),
    params
  );
}
